// Loosely based on: https://github.com/auth0/auth0-react/
import { createContext } from "react";
import type { AuthenticateResult, AuthUserResult, ResendMagicLinkResult, SendMagicLinkResult } from "@medbillai/graphql-types";
import type { OAuthProviderType } from "@medbillai/ui";
import { getStartedUrl, loginUrl } from "@medbillai/utils";
import type { StytchTokenType } from "../../provider/stytch";
import type { UserSegmentationData } from "../sign-up-onboarding/signUpOnboardingStore";
import { type AuthState, initialAuthState, type User } from "./authState";
export const authPaths = [loginUrl(),
// These paths require params to be passed in so we use the string instead of
// the function from utils.
"/verify-email", "/authenticate", getStartedUrl()];
export interface AuthContextInterface<TUser extends User = User> extends AuthState<TUser> {
  startOAuth: (provider: OAuthProviderType) => Promise<void>;
  sendMagicLink: (params: {
    email: string;
    botControlToken?: string;
    userSegmentationData?: UserSegmentationData;
  }) => Promise<SendMagicLinkResult>;
  resendMagicLink: (params: {
    email: string;
  }) => Promise<ResendMagicLinkResult>;
  authenticate: (params: {
    method: StytchTokenType;
    token: string;
    codeVerifier?: string;
    userSegmentationData?: UserSegmentationData;
  }) => Promise<AuthenticateResult>;
  logout: () => Promise<void>;
  shadowLogin: (email: string) => Promise<AuthUserResult | undefined>;
  finishOnboarding: () => void;
  setIntendedPath: (path: string) => void;
  getAndClearIntendedPath: () => string | null;
}
export const initialContext: AuthContextInterface = {
  ...initialAuthState,
  // Functions will be set by AuthProvider upon instantiation
  // startOAuth starts the OAuth flow for the given provider.
  startOAuth: () => {
    throw new Error("Context not initialized");
  },
  // sendMagicLink sends a magic login link to the given email address.
  sendMagicLink: () => {
    throw new Error("Context not initialized");
  },
  resendMagicLink: () => {
    throw new Error("Context not initialized");
  },
  // authenticate authenticates the user with either magic link or OAuth token.
  authenticate: () => {
    throw new Error("Context not initialized");
  },
  // logout logs the user out through the API. The user's session cookie is cleared as a result.
  logout: () => {
    throw new Error("Context not initialized");
  },
  // shadowLogin allows an admin user to convert their session to impersonate another user.
  shadowLogin: () => {
    throw new Error("Context not initialized");
  },
  finishOnboarding: () => {
    throw new Error("Context not initialized");
  },
  setIntendedPath: () => {
    throw new Error("Context not initialized");
  },
  getAndClearIntendedPath: () => {
    throw new Error("Context not initialized");
  }
};
export const AuthContext = createContext<AuthContextInterface>(initialContext);